// View as, for InRange: the menu beside Share that reloads the overview as someone on its
// share list sees it, and the banner shown while viewing as them
// (docs/Y2026-Q4/design-amazon-item-owners.md section 2.8).
import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import type { OverviewPerson } from "../../api/hooks/useAmazonOwners";
import { exitViewAs, startViewAs } from "../../api/viewAs";
import { UserContext } from "../../auth";
import styles from "./viewAs.module.css";

export const ViewAsButton = ({ people }: { people: OverviewPerson[] }) => {
  const { role } = useContext(UserContext);
  // Stays on this page, without its query: that holds InRange's own switches (whose items,
  // every site, edit), and the page opens as the person would open it (Noaa, 25 Oct 2026).
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  if (role !== "admin") return null;
  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.button}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        View as
      </button>
      {open && (
        <div className={styles.menu} role="menu">
          <div className={styles.muted}>See the page as they see it. Read-only.</div>
          {people.length === 0 && <div className={styles.muted}>Nobody is on the share list yet.</div>}
          {people.map((p) => (
            <button
              key={p.person_id}
              type="button"
              role="menuitem"
              className={styles.option}
              onClick={() => p.email && startViewAs({ email: p.email, name: p.name }, pathname)}
            >
              <span>{p.name}</span>
              {p.email && p.email !== p.name && <span className={styles.muted}>{p.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** The strip on top while staff view as someone; nothing here writes. */
export const ViewAsBanner = () => {
  const { viewingAs } = useContext(UserContext);
  if (!viewingAs) return null;
  return (
    <div className={styles.banner} role="status">
      Viewing as {viewingAs.name} ({viewingAs.email}). Read-only: nothing you do here is saved.{" "}
      <button type="button" className={styles.exit} onClick={exitViewAs}>
        Exit
      </button>
    </div>
  );
};

/** Why a write is unavailable while viewing as someone, or null when it is not. */
export const useReadOnly = (): string | null => {
  const { viewingAs } = useContext(UserContext);
  return viewingAs ? `Read-only while viewing as ${viewingAs.name}` : null;
};
